/**
 * v0.18.94 — 「默认过期时间 = 今天 + 1 年」公用算法 (辉哥 10/25 14:36 拍板)
 *
 * ## 为什么抽成公用文件 (本仓铁律: 一个逻辑一个入口)
 *   「今天 + 1 年」这个算法本批有**两个**调用方:
 *     ① `ConvertStudentModal.js` —— 转学员办卡 modal 的「开卡」块
 *     ② `BindCardModal.js`       —— 绑卡 modal 的「立即开卡」块
 *   两边各抄一份 ⇒ 将来改口径 (比如「+ 1 年」改成「+ 12 个月」) 必然漏改一边
 *   ⇒ 抽到这里, 两边 import。
 *
 * ## ⚠️ 2 月 29 日是**真实边界** (⛔ 不许用 `setFullYear` 了事)
 *   `new Date(2028, 1, 29).setFullYear(2029)` ⇒ JS 会**自动进位**成 `2029-03-01`
 *   (2 月 29 日在 2029 年不存在) ⇒ 卡片会多出一天。
 *   ⇒ 正确口径: `2028-02-29` + 1 年 = **`2029-02-28`** (回退到当月最后一天)。
 *   算法 = 「先算目标年当月的最后一天, 再取 min(今天几号, 那个最后一天)」。
 *
 *   例:
 *     2026-10-25 ⇒ 2027-10-25   (普通月)
 *     2028-02-29 ⇒ 2029-02-28   ⚠️ 闰日回退 (不是 2029-03-01)
 *     2027-02-28 ⇒ 2028-02-28   (目标年是闰年, 但今天不是 29 号 ⇒ 不进位)
 *     2028-01-31 ⇒ 2029-01-31   (31 号的月也一样, 只看目标年当月最后一天)
 */

/**
 * 「今天 + 1 年」⇒ `YYYY-MM-DD`
 *
 * @param {Date} [now] - 基准日期 (⛔ 默认 = 真实「现在」; 测试可喂假日期验 2/29 边界)
 * @returns {string} `YYYY-MM-DD`
 */
export function defaultExpiresAt(now) {
  const base = now instanceof Date ? now : new Date();
  const y = base.getFullYear();
  const m = base.getMonth(); // 0-11
  const d = base.getDate();
  // 目标年 (y+1) 当月的最后一天 —— `new Date(y, m+1, 0)` = 下个月第 0 天 = 本月最后一天
  const lastDay = new Date(y + 1, m + 1, 0).getDate();
  // ⛔ 用 min 而不是让 JS 自己进位 (见文件头注释的 2/29 说明)
  const day = Math.min(d, lastDay);
  const pad = n => String(n).padStart(2, '0');
  return `${y + 1}-${pad(m + 1)}-${pad(day)}`;
}

/**
 * 「今天 + 1 年」⇒ **本地零点**的 `Date` (flatpickr 的 `defaultDate` 用)
 *
 * ⚠️ 为什么要转成 Date:
 *   flatpickr 的 `defaultDate` 收的是 Date 对象; 直接塞字符串 `'2027-10-25'`
 *   会被 `new Date('2027-10-25')` 解析成 **UTC 零点** ⇒ 在东八区显示成
 *   `2027-10-25 08:00` ⇒ 日期框里可能显示成 10-25 但日历高亮的是同一天 (时区边界)。
 *   ⇒ 这里显式构造**本地零点**的 Date, 与 `defaultExpiresAt()` 的口径逐字一致。
 *
 * @param {Date} [now] - 基准日期
 * @returns {Date} 本地零点的 Date
 */
export function defaultExpiresAtDate(now) {
  const iso = defaultExpiresAt(now);
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!m) return new Date();
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
}
flarum.reg.add('ziiven-ziven-scheduler', 'forum/utils/DefaultExpiry', { defaultExpiresAt: defaultExpiresAt,defaultExpiresAtDate: defaultExpiresAtDate, });